import { useTranslation } from "react-i18next";
import { formatSource } from "@validator/schema";

import { Alert } from "@shared/components/Alert";
import { SecondaryButton } from "@shared/components/Button";
import { Option as CheckboxOption } from "@shared/components/form/Checkbox";
import { Option as RadioOption } from "@shared/components/form/Radio";

import { StudyContextData, SubmissionContextData } from "@/context/AppContext";
import { SubmissionFailure } from "@/types/submission";

import { SubmissionForm } from "@/views/study/components/submission/SubmissionForm";

import { T } from "@/components/i18n";
import { StudyFileUploadHelp } from "@/components/help/StudyHelp";

import { AlertTriangle, CircleX, FileExclamationPoint, Globe } from "lucide-react";
import { effectiveStudy, isNonValidatedLinkedStudy } from "@shared/study";

interface InvalidDataFlowProps {
  studyContext: StudyContextData;
  submissionContext: SubmissionContextData;
}

function SafeFailureAlert({ failure }: { failure: SubmissionFailure }) {
  const source = failure.source;

  if (source === "unknown") {
    return <T i18nKey="view.submission.flow.invalidData.safeFailure.unknownSourceAlert" />;
  }

  return (
    <T
      i18nKey="view.submission.flow.invalidData.safeFailure.alert"
      values={{
        source: formatSource(source),
      }}
    />
  );
}

export function InvalidDataFlow({ submissionContext, studyContext }: InvalidDataFlowProps) {
  const { t } = useTranslation();
  const { writeState, validation, resetValidation, setOverrideValidation, setUserSource } =
    submissionContext;

  const { study } = studyContext;

  if (!study) {
    return null;
  }

  const noValidation = studyContext.study && isNonValidatedLinkedStudy(studyContext.study);
  const rejected = validation?.status === "rejected" ? validation : null;
  const baselineDays = effectiveStudy(study).baselineChatHistoryWindowDays;

  if (
    writeState.status === "validation_error" &&
    rejected?.failure.reason === "empty_after_truncation" &&
    baselineDays != null
  ) {
    const { failure, overrideValidation } = rejected;
    return (
      <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
        <div className="mb-4">
          <Alert
            variant="warning"
            status={t("view.submission.flow.invalidData.emptyAfterTruncationFailure.status")}
            text={
              <T
                i18nKey="view.submission.flow.invalidData.emptyAfterTruncationFailure.alert"
                count={baselineDays}
                values={{ source: formatSource(failure.source) }}
              />
            }
            Icon={AlertTriangle}
          />
        </div>
        <p className="mb-4 font-semibold">
          <T i18nKey="view.submission.flow.invalidData.emptyAfterTruncationFailure.warning" />
        </p>
        <p className="mb-4">
          <T i18nKey="view.submission.flow.invalidData.emptyAfterTruncationFailure.instructions" />
        </p>
        <div className="flex gap-x-3 items-center w-full mb-4">
          <CheckboxOption
            checked={overrideValidation}
            onChange={() => setOverrideValidation(!overrideValidation)}
          >
            Use my export anyway
          </CheckboxOption>
          <span className="font-bold text-sm">or</span>
          <SecondaryButton variant="secondary" round highlight onClick={resetValidation}>
            Try another file
          </SecondaryButton>
        </div>
        <div className="mt-2 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyFileUploadHelp studyId={study.id} invalidFlow />
        </div>
      </SubmissionForm>
    );
  }

  if (
    writeState.status === "validation_error" &&
    rejected?.failure.reason === "low_message_volume"
  ) {
    const { failure, overrideValidation } = rejected;
    return (
      <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
        <div className="mb-4">
          <Alert
            variant="warning"
            status={t("view.submission.flow.invalidData.lowMessageVolumeFailure.status")}
            text={
              <T
                i18nKey="view.submission.flow.invalidData.lowMessageVolumeFailure.alert"
                values={{
                  source: formatSource(failure.source),
                }}
              />
            }
            Icon={AlertTriangle}
          />
        </div>
        <p className="mb-4 font-semibold">
          <T i18nKey="view.submission.flow.invalidData.lowMessageVolumeFailure.warning" />
        </p>
        <p className="mb-4">
          <T i18nKey="view.submission.flow.invalidData.lowMessageVolumeFailure.instructions" />
        </p>
        <p className="mb-4 text-sm">
          <T i18nKey="view.submission.flow.invalidData.lowMessageVolumeFailure.note" />
        </p>
        <div className="flex gap-x-3 items-center w-full mb-4">
          <CheckboxOption
            checked={overrideValidation}
            onChange={() => setOverrideValidation(!overrideValidation)}
          >
            Use my export anyway
          </CheckboxOption>
          <span className="font-bold text-sm">or</span>
          <SecondaryButton variant="secondary" round highlight onClick={resetValidation}>
            Try another file
          </SecondaryButton>
        </div>
        <div className="mt-2 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyFileUploadHelp studyId={study.id} invalidFlow />
        </div>
      </SubmissionForm>
    );
  }

  if (writeState.status === "validation_error" && rejected?.failure.overridable) {
    const { failure, overrideValidation, userSource } = rejected;
    const isUnknownSafeFailure = failure.source === "unknown";

    return (
      <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
        <div className="mb-4">
          <Alert
            variant="warning"
            status={t("view.submission.flow.invalidData.safeFailure.status")}
            text={<SafeFailureAlert failure={failure} />}
            Icon={AlertTriangle}
          />
        </div>
        <p className="mb-4 font-semibold">
          <T i18nKey="view.submission.flow.invalidData.safeFailure.warning" />
        </p>
        {noValidation && isUnknownSafeFailure ? (
          <p className="mb-4">
            <T i18nKey="view.submission.flow.invalidData.safeFailure.unknownSourceInstructions" />
          </p>
        ) : (
          <p className="mb-4">
            <T i18nKey="view.submission.flow.invalidData.safeFailure.instructions" />
          </p>
        )}

        {noValidation && isUnknownSafeFailure && (
          <label className="mb-6 px-4 py-4 border border-neutral-200 dark:border-neutral-800 rounded-xl">
            <span className="mb-3 flex items-center font-semibold gap-x-1">
              <Globe size={16} className="stroke-blue-500" />
              Choose your service:
            </span>
            <fieldset className="flex gap-x-2">
              <RadioOption
                name="source"
                checked={userSource === "chatgpt"}
                onChange={() => setUserSource("chatgpt")}
              >
                ChatGPT
              </RadioOption>
              <RadioOption
                name="source"
                checked={userSource === "claude"}
                onChange={() => setUserSource("claude")}
              >
                Claude
              </RadioOption>
              <RadioOption
                name="source"
                checked={userSource === "gemini"}
                onChange={() => setUserSource("gemini")}
              >
                Gemini
              </RadioOption>
            </fieldset>
          </label>
        )}
        <div className="flex gap-x-3 items-center w-full mb-4">
          <CheckboxOption
            checked={overrideValidation}
            onChange={() => setOverrideValidation(!overrideValidation)}
          >
            Use my full export
          </CheckboxOption>
          <span className="font-bold text-sm">or</span>
          <SecondaryButton variant="secondary" round highlight onClick={resetValidation}>
            Try another file
          </SecondaryButton>
        </div>
        <div className="mt-2 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyFileUploadHelp studyId={study.id} invalidFlow />
        </div>
      </SubmissionForm>
    );
  }

  if (
    writeState.status === "validation_error" &&
    rejected?.failure.reason === "claude_conversations_missing"
  ) {
    return (
      <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
        <Alert
          variant="error"
          status={t("view.submission.flow.invalidData.missingFailure.status")}
          text={<T i18nKey="view.submission.flow.invalidData.missingFailure.alert" />}
          Icon={CircleX}
          errors={writeState.status === "validation_error" ? writeState.errors : null}
        >
          <div className="-mt-2">
            <SecondaryButton variant="secondary" round highlight onClick={resetValidation}>
              Try another file
            </SecondaryButton>
          </div>
        </Alert>
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyFileUploadHelp studyId={study.id} />
        </div>
      </SubmissionForm>
    );
  }

  if (writeState.status === "validation_error" || writeState.status === "file_error") {
    return (
      <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
        <Alert
          variant="error"
          status={
            writeState.status === "file_error"
              ? t("view.submission.flow.invalidData.otherFailure.fileErrorStatus")
              : t("view.submission.flow.invalidData.otherFailure.validationErrorStatus")
          }
          text={
            writeState.status === "file_error"
              ? t("view.submission.flow.invalidData.otherFailure.fileErrorAlert")
              : t("view.submission.flow.invalidData.otherFailure.validationErrorAlert")
          }
          message={writeState.message}
          Icon={writeState.status === "file_error" ? FileExclamationPoint : CircleX}
          errors={writeState.status === "validation_error" ? writeState.errors : null}
        >
          <div className="-mt-2">
            <SecondaryButton variant="secondary" round highlight onClick={resetValidation}>
              Try another file
            </SecondaryButton>
          </div>
        </Alert>
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyFileUploadHelp studyId={study.id} />
        </div>
      </SubmissionForm>
    );
  }

  return null;
}
